import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Activity,
  ArrowRight,
  CheckCircle2,
  Mail,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { PasswordInput } from "@/components/PasswordInput";
import { toast } from "sonner";

const HCAPTCHA_LOADER_ID = "hcaptcha-script";

function ensureHCaptchaScript() {
  return new Promise((resolve) => {
    if (window.hcaptcha) {
      resolve();
      return;
    }
    const existing = document.getElementById(HCAPTCHA_LOADER_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      return;
    }
    const s = document.createElement("script");
    s.id = HCAPTCHA_LOADER_ID;
    s.src = "https://js.hcaptcha.com/1/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    document.head.appendChild(s);
  });
}

// Phase E2 — Format a plan price into a single scannable line.
function formatPlanPrice(plan, t) {
  if (!plan) return "";
  const metric = plan.billing_metric;
  const minMonthly = Number(plan.min_monthly_usd || 0);
  const perUnit = Number(plan.price_per_unit_usd || 0);
  const flat = Number(plan.price_monthly_usd || 0);
  if (metric === "flat") {
    return flat <= 0 ? t("signup.freeLabel") : `$${flat.toFixed(0)}${t("signup.perMonth")}`;
  }
  const unitSuffix =
    metric === "per_extension" ? t("signup.perExtMonth") : t("signup.perChannelMonth");
  const base = `$${perUnit.toFixed(2)}${unitSuffix}`;
  return minMonthly > 0
    ? `${base} · ${t("signup.min")} $${minMonthly.toFixed(0)}`
    : base;
}

export default function SignupPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orgName, setOrgName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tosAccepted, setTosAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [enabled, setEnabled] = useState(null);
  const [captchaCfg, setCaptchaCfg] = useState(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const [plans, setPlans] = useState([]);
  const [planSlug, setPlanSlug] = useState("");
  const captchaWidgetRef = useRef(null);
  const captchaContainerRef = useRef(null);

  useEffect(() => {
    let aborted = false;
    api
      .get("/public/signup-enabled")
      .then((r) => {
        if (aborted) return;
        setEnabled(!!r.data.enabled);
        if (r.data?.default_plan) {
          setPlanSlug((curr) => curr || r.data.default_plan);
        }
      })
      .catch(() => !aborted && setEnabled(false));
    api
      .get("/auth/captcha-config")
      .then((r) => !aborted && setCaptchaCfg(r.data || { enabled: false }))
      .catch(() => !aborted && setCaptchaCfg({ enabled: false }));
    api
      .get("/billing/public-plans")
      .then((r) => {
        if (aborted) return;
        const list = r.data?.plans || [];
        setPlans(list);
        setPlanSlug((curr) => {
          if (curr && list.some((p) => p.slug === curr)) return curr;
          return list[0]?.slug || curr;
        });
      })
      .catch(() => !aborted && setPlans([]));
    return () => {
      aborted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render the hCaptcha widget once both config and DOM are ready.
  useEffect(() => {
    if (!captchaCfg?.enabled || !captchaCfg?.sitekey) return;
    if (captchaWidgetRef.current !== null) return;
    let cancelled = false;
    ensureHCaptchaScript().then(() => {
      if (cancelled) return;
      const renderIt = () => {
        if (!window.hcaptcha || !captchaContainerRef.current) {
          setTimeout(renderIt, 200);
          return;
        }
        try {
          captchaWidgetRef.current = window.hcaptcha.render(
            captchaContainerRef.current,
            {
              sitekey: captchaCfg.sitekey,
              theme: "dark",
              callback: (tok) => setCaptchaToken(tok),
              "expired-callback": () => setCaptchaToken(""),
              "error-callback": () => setCaptchaToken(""),
            }
          );
        } catch (e) {
          // already rendered or sitekey invalid — ignore
        }
      };
      renderIt();
    });
    return () => {
      cancelled = true;
    };
  }, [captchaCfg]);

  const selectedPlan = useMemo(
    () => plans.find((p) => p.slug === planSlug) || null,
    [plans, planSlug]
  );

  if (user) return <Navigate to={user.scope === "platform" ? "/platform" : "/"} replace />;

  const passwordOk = password.length >= 10;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!tosAccepted) {
      toast.error(t("signup.tosRequired"));
      return;
    }
    if (!passwordOk) {
      toast.error(t("signup.passwordTooShort"));
      return;
    }
    if (captchaCfg?.enabled && !captchaToken) {
      toast.error(t("signup.captchaRequired"));
      return;
    }
    if (plans.length > 0 && !planSlug) {
      toast.error(t("signup.selectAPlan"));
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/auth/signup", {
        org_name: orgName,
        email,
        password,
        captcha_token: captchaToken || undefined,
        tos_accepted: tosAccepted,
        tos_version: "v1.0",
        plan_slug: planSlug || undefined,
      });
      toast.success(t("signup.signupSuccess"));
      setDone(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("signup.signupFailed"));
      if (window.hcaptcha && captchaWidgetRef.current !== null) {
        try { window.hcaptcha.reset(captchaWidgetRef.current); } catch { /* noop */ }
      }
      setCaptchaToken("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-xl space-y-6">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center gap-2 mb-2">
            <Activity className="h-6 w-6 text-[hsl(var(--accent))]" />
            <span className="text-xl font-bold tracking-tight">Elswitch</span>
          </Link>
          <h1 className="text-lg font-semibold">{t("signup.title")}</h1>
        </div>

        {enabled === false ? (
          <div className="rounded-lg border border-border bg-card p-4 text-center text-sm text-muted-foreground">
            {t("signup.selfDisabled")}
          </div>
        ) : done ? (
          <div className="rounded-lg border border-border bg-card p-6 text-center space-y-3">
            <CheckCircle2 className="h-10 w-10 text-[hsl(var(--ok))] mx-auto" />
            <h2 className="text-lg font-semibold">{t("signup.almostThere")}</h2>
            <p className="text-sm text-muted-foreground inline-flex items-start gap-2 justify-center">
              <Mail className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--accent))]" />
              <span>{t("signup.verifySent", { email })}</span>
            </p>
            <p className="text-[11px] text-muted-foreground">
              {t("signup.pendingNote", { status: "pending" })}
            </p>
            <Button onClick={() => navigate("/login")} className="mt-2" data-testid="signup-goto-login">
              {t("signup.continueLogin")} <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="rounded-lg border border-border bg-card p-5 space-y-4" data-testid="signup-form">
            <div>
              <Label>{t("signup.orgLabel")}</Label>
              <Input
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                data-testid="signup-org"
                required
              />
            </div>
            <div>
              <Label>{t("signup.emailLabel")}</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="signup-email"
                required
              />
            </div>
            <div>
              <Label>
                {t("signup.passwordLabel")}{" "}
                <span className="text-[10px] text-muted-foreground font-normal">
                  {t("signup.passwordHint")}
                </span>
              </Label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={10}
                data-testid="signup-password"
                required
              />
              {password && !passwordOk && (
                <p className="text-[11px] text-[hsl(var(--warn))] mt-1">
                  {t("signup.passwordTooShort")}
                </p>
              )}
            </div>

            {plans.length > 0 && (
              <div className="space-y-2" data-testid="signup-plan-picker">
                <div className="flex items-center justify-between">
                  <Label className="m-0">{t("signup.pickPlan")}</Label>
                  <Link
                    to="/pricing"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[11px] text-accent hover:underline inline-flex items-center gap-1"
                  >
                    <Sparkles className="h-3 w-3" /> {t("signup.compareAll")}
                  </Link>
                </div>
                <div role="radiogroup" className="grid gap-2 sm:grid-cols-2">
                  {plans.map((p) => {
                    const active = p.slug === planSlug;
                    return (
                      <button
                        type="button"
                        role="radio"
                        aria-checked={active}
                        key={p.slug}
                        onClick={() => setPlanSlug(p.slug)}
                        data-testid={`signup-plan-${p.slug}`}
                        className={
                          "text-left rounded-md border p-3 transition-colors " +
                          "focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))] " +
                          (active
                            ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent)/0.08)]"
                            : "border-border bg-background hover:bg-muted/40")
                        }
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-sm">{p.name}</span>
                          {active && (
                            <Badge variant="outline" className="text-[10px] py-0 h-4 border-[hsl(var(--accent))] text-[hsl(var(--accent))]">
                              {t("signup.selected")}
                            </Badge>
                          )}
                        </div>
                        <div className="text-[12px] text-foreground mt-1 font-mono">
                          {formatPlanPrice(p, t)}
                        </div>
                        {p.description && (
                          <div className="text-[11px] text-muted-foreground mt-1 leading-snug line-clamp-2">
                            {p.description}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                {selectedPlan && (
                  <p className="text-[11px] text-muted-foreground">
                    {t("signup.changePlanLater", { path: "Billing → My plan" })}
                  </p>
                )}
              </div>
            )}

            <label className="flex items-start gap-2 cursor-pointer text-xs text-muted-foreground">
              <Checkbox
                checked={tosAccepted}
                onCheckedChange={(v) => setTosAccepted(!!v)}
                data-testid="signup-tos"
                className="mt-0.5"
              />
              <span>
                {t("signup.tosPrefix")}{" "}
                <a href="/terms" target="_blank" rel="noreferrer" className="text-accent hover:underline">
                  {t("signup.tos")}
                </a>{" "}
                {t("signup.tosAnd")}{" "}
                <a href="/privacy" target="_blank" rel="noreferrer" className="text-accent hover:underline">
                  {t("signup.privacy")}
                </a>
                {t("signup.tosSuffix")}
              </span>
            </label>

            {captchaCfg?.enabled && (
              <div
                ref={captchaContainerRef}
                data-testid="hcaptcha-widget"
                className="flex justify-center"
              />
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={
                loading
                || !tosAccepted
                || !passwordOk
                || (captchaCfg?.enabled && !captchaToken)
                || (plans.length > 0 && !planSlug)
              }
              data-testid="signup-submit"
            >
              <ShieldCheck className="h-4 w-4 mr-1" />
              {loading ? t("signup.submitting") : t("signup.submit")}
            </Button>

            <p className="text-[10px] text-muted-foreground text-center">
              {t("signup.logNotice")}
            </p>

            <div className="text-xs text-muted-foreground text-center pt-3 border-t border-border">
              {t("signup.haveAccount")}{" "}
              <Link to="/login" className="text-accent hover:underline">
                {t("signup.signIn")}
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
